import React, { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import { storeActivityIndicator, storeAreYouSureModalCallback } from '../../../../redux/reducers/authSlice';
import { OtomatikFaturaOnizleme, OtomatikFaturaService, tl } from '../../../../services/OtomatikFaturaService';

interface Props {
    isShown: boolean
    kuralId: string
    kuralAdi: string
    onClose: () => void
    onCalisti: () => void
}

/**
 * Önizle / Şimdi çalıştır: kural seçilen gün çalışmış gibi dönemini hesaplar ve oluşacak faturaları gösterir; kayıt
 * oluşmaz. "Bu dönemi şimdi çalıştır" aynı dönemi iş kuyruğuna alır (dönem daha önce faturalandıysa sunucu reddeder).
 */
/** Hucre stilleri: sayi kolonlari saga dayali, tek satir; metin kolonlari kelimeden kirilir (yatay kaydirma olmasin). */
const HUCRE: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.5rem', whiteSpace: 'normal', wordBreak: 'break-word' };
const SAYI: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap' };
/** Baslik satiri: liste ekranlarindaki standart renk (new-table-container thead: #8DC1FF, siyah yazi). */
const RENK: React.CSSProperties = { background: '#8DC1FF', color: '#000' };
/** Not kolonu: ince cizgiyle ayrilir, saga dayali Brut Tutar ile arasinda bosluk kalir. */
const NOT_KOLONU: React.CSSProperties = { paddingLeft: '1.25rem', borderLeft: '1px solid #E4E6EF' };

/** Sayi basliklari: saga dayali, gerekirse iki satira kirilir ("Tevkifat / Tutari"); degerlerle ayni sag bosluk. */
const SAYI_BASLIK: React.CSSProperties = { ...SAYI, whiteSpace: 'normal', verticalAlign: 'bottom', lineHeight: 1.3 };

const gun = (t: string | undefined) => t ? t.slice(0, 10).split('-').reverse().join('.') : '';

const OnizlemeModal = ({ isShown, kuralId, kuralAdi, onClose, onCalisti }: Props) => {
    const dispatch = useDispatch();
    const [tarih, setTarih] = useState<string | undefined>(new Date().toISOString().slice(0, 10));
    const [sonuc, setSonuc] = useState<OtomatikFaturaOnizleme | undefined>(undefined);

    useEffect(() => {
        if (isShown) {
            setSonuc(undefined);
            onizle(tarih);
        }
    }, [isShown])

    const onizle = async (_tarih: string | undefined) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OtomatikFaturaService.onizle(kuralId, _tarih);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setSonuc(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const calistir = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await OtomatikFaturaService.simdiCalistir(kuralId, tarih);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            toast.success(`${response.data.donemAdi} çalışması kuyruğa alındı.`);
            onClose();
            onCalisti();
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const faturalar = sonuc?.satirlar.filter(x => !x.atlanacak) ?? [];

    return (
        <Modal show={isShown} onHide={onClose} size="xl" centered dialogClassName="otofatura-onizleme">
            <Modal.Header closeButton>
                <Modal.Title>Önizleme · {kuralAdi}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-end gap-3 mb-5">
                    <div style={{ width: 220 }}>
                        <CustomDatePicker labelText="Çalışma tarihi" value={tarih} setValue={setTarih} />
                    </div>
                    <button className="btn btn-sm btn-light-primary" onClick={() => onizle(tarih)}>Hesapla</button>
                    {
                        sonuc && (
                            <div className="text-gray-700 fs-6 ms-3">
                                Dönem <b>{sonuc.donemAdi}</b> ({gun(sonuc.donemBaslangic)} – {gun(sonuc.donemBitis)}) · fatura tarihi <b>{sonuc.faturaTarihi?.slice(0, 10).split('-').reverse().join('.')}</b>
                                {sonuc.mevcutCalismaDurumu && (sonuc.silinenFaturaSayisi > 0
                                    ? <span className="badge badge-light-primary ms-2">Önceki çalışmanın {sonuc.silinenFaturaSayisi} faturası silinmiş; yeniden oluşturulabilir</span>
                                    : <span className="badge badge-light-warning ms-2">Bu dönem için çalışma var: {sonuc.mevcutCalismaDurumu}</span>)}
                            </div>
                        )
                    }
                </div>

                {
                    sonuc && (
                        <React.Fragment>
                            {/* Kolonlar faturadaki adlarla: Toplam Tutar (KDV hariç), KDV Tutarı, Tevkifat Tutarı, Brüt Tutar (ödenecek).
                                Sabit genişlikler: yatay kaydırma olmasın, başlıklar değerlerle hizalı. */}
                            <table className='table align-middle table-row-dashed ebbe-compact-table fs-7 otofatura-onizleme-tablo' style={{ tableLayout: 'fixed', width: '100%' }}>
                                <colgroup>
                                    {/* Açıklama geniş: sabit gelirde bölüm / hizmet kalemi satırları tutarlarıyla altında (27.10.2026). */}
                                    <col style={{ width: '17%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '22%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '14%' }} />
                                </colgroup>
                                <thead>
                                    <tr className='fw-bolder text-uppercase'>
                                        <th style={{ ...HUCRE, ...RENK, verticalAlign: 'bottom' }}>Müşteri</th>
                                        <th style={{ ...HUCRE, ...RENK, verticalAlign: 'bottom' }}>PO No</th>
                                        <th style={{ ...HUCRE, ...RENK, verticalAlign: 'bottom' }}>Açıklama</th>
                                        <th className='sayi' style={{ ...SAYI_BASLIK, ...RENK }}>Toplam Tutar</th>
                                        <th className='sayi' style={{ ...SAYI_BASLIK, ...RENK }}>KDV Tutarı</th>
                                        <th className='sayi' style={{ ...SAYI_BASLIK, ...RENK }}>Tevkifat Tutarı</th>
                                        <th className='sayi' style={{ ...SAYI_BASLIK, ...RENK }}>Brüt Tutar</th>
                                        <th className='not-kolonu' style={{ ...HUCRE, ...RENK, ...NOT_KOLONU, verticalAlign: 'bottom' }}>Not</th>
                                    </tr>
                                </thead>
                                <tbody className='text-gray-700 fw-semibold'>
                                    {
                                        sonuc.satirlar.length == 0 && (
                                            <tr><td className="text-center" colSpan={99}>Bu dönem için faturalanacak kayıt yok.</td></tr>
                                        )
                                    }
                                    {
                                        sonuc.satirlar.map(s => (
                                            <tr key={s.grupAnahtari} className={s.atlanacak ? 'text-muted' : undefined} style={s.atlanacak ? { textDecoration: 'line-through' } : undefined}>
                                                <td style={HUCRE}>{s.musteriAdi}</td>
                                                <td style={HUCRE}>{s.poNo}</td>
                                                <td style={HUCRE}>
                                                    {s.aciklama}
                                                    {s.tevkifatli && <div><span className="badge badge-light-info">tevkifatlı</span></div>}
                                                    {
                                                        (s.detaylar ?? []).length > 0 && (
                                                            <div className="mt-1 fs-8 text-gray-600">
                                                                {s.detaylar!.map((d, i) => (
                                                                    <div key={i} className="d-flex justify-content-between gap-2">
                                                                        <span>{d.bolum ?? d.kalem ?? '—'}</span>
                                                                        <span style={{ whiteSpace: 'nowrap' }}>{tl(d.tutar)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )
                                                    }
                                                </td>
                                                <td className='sayi' style={SAYI}>{tl(s.matrah)}{s.paraBirimi && s.paraBirimi != 'TRY' ? ' ' + s.paraBirimi : ''}</td>
                                                <td className='sayi' style={SAYI}>{tl(s.kdv)}</td>
                                                <td className='sayi' style={SAYI}>{s.tevkifat ? tl(s.tevkifat) : '—'}</td>
                                                <td className='sayi' style={{ ...SAYI, fontWeight: 700 }}>{tl(s.brut)}</td>
                                                <td className='not-kolonu' style={{ ...HUCRE, ...NOT_KOLONU, textDecoration: 'none' }}>{s.not}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                {
                                    faturalar.length > 0 && (
                                        <tfoot>
                                            <tr className="ebbe-dip-toplam">
                                                <td style={HUCRE} colSpan={3}>{faturalar.length} fatura</td>
                                                <td className='sayi' style={SAYI}>{tl(sonuc.matrah)}</td>
                                                <td className='sayi' style={SAYI}>{tl(sonuc.kdv)}</td>
                                                <td className='sayi' style={SAYI}>{sonuc.tevkifat ? tl(sonuc.tevkifat) : '—'}</td>
                                                <td className='sayi' style={{ ...SAYI, fontWeight: 700 }}>{tl(sonuc.brut)}</td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )
                                }
                            </table>

                            {
                                sonuc.uyarilar.length > 0 && (
                                    <div className="alert alert-warning fs-7 py-3">
                                        {sonuc.uyarilar.map((u, i) => <div key={i}>{u}</div>)}
                                    </div>
                                )
                            }

                            <div className="text-muted fs-7">Tevkifat, fatura oluşurken kullanılan kuralla tahmin edilir (müşteri tevkifata tabi, hizmetin tevkifat oranı, alt limit); kesin tutar faturada. Brüt Tutar = Toplam Tutar + KDV − Tevkifat (ödenecek). Üstü çizili satırlar faturalanmaz.</div>
                        </React.Fragment>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-sm btn-light" onClick={onClose}>Kapat</button>
                <button className="btn btn-sm btn-primary" disabled={!sonuc || faturalar.length == 0 || !!sonuc.mevcutCalismaDurumu && !sonuc.yenidenCalistirilabilir}
                    onClick={() => dispatch(storeAreYouSureModalCallback(calistir))}>
                    Bu dönemi şimdi çalıştır
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default OnizlemeModal;
